import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import InputFormField from 'src/registry/components/forms/InputFormField';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';
import { Form, FormDescription } from 'src/registry/ui/Form';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { useListUnits } from 'src/features/units/api/useListUnits';
import { unitsMessages } from 'src/features/units/utils/messages';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS } from 'src/features/moneyAccounts/utils/constants';
import { billableMetricsMessages } from 'src/features/billableMetrics/utils/messages';
import { BillableMetricFormSchemaValues } from 'src/features/billableMetrics/utils/formSchema';
import { MEASUREMENT_TYPE } from 'src/features/billableMetrics/utils/types';
import {
    AGGREGATION_TYPE_MAP,
    MEASUREMENT_TYPE_MAP,
    VALID_AGGREGATIONS_BY_MEASUREMENT,
} from 'src/features/billableMetrics/utils/constants';

type BillableMetricFormProps = {
    form: UseFormReturn<BillableMetricFormSchemaValues>;
    onSubmit: (values: BillableMetricFormSchemaValues) => void;
};

function BillableMetricForm({ onSubmit, form }: BillableMetricFormProps) {
    const { formatMessage } = useIntl();

    const { data: units } = useListUnits({});

    /*
     * The valid aggregations depend on the measurement type - sending a mismatched pair is a
     * `400 invalid_aggregation_for_measurement_type`. `unique_count` is in neither list because the
     * backend always rejects it.
     */
    const measurementType = form.watch('measurement_type');
    const aggregationOptions = VALID_AGGREGATIONS_BY_MEASUREMENT[measurementType] ?? [];

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<BillableMetricFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(commonMessages.name)}
            />
            <InputFormField<BillableMetricFormSchemaValues>
                form={form}
                name="code"
                label={formatMessage(unitsMessages.code)}
            />
            <SelectFormField<BillableMetricFormSchemaValues>
                form={form}
                name="measurement_type"
                label={formatMessage(billableMetricsMessages.measurementType)}
                options={Object.values(MEASUREMENT_TYPE)}
                optionTranslationMap={MEASUREMENT_TYPE_MAP}
                onChange={(value) => {
                    // Without this reset, switching type leaves an aggregation the API will reject.
                    const [firstValid] = VALID_AGGREGATIONS_BY_MEASUREMENT[value as MEASUREMENT_TYPE];
                    form.setValue('aggregation_type', firstValid, { shouldDirty: true });
                }}
            />
            <SelectFormField<BillableMetricFormSchemaValues>
                form={form}
                name="aggregation_type"
                label={formatMessage(billableMetricsMessages.aggregationType)}
                options={aggregationOptions}
                optionTranslationMap={AGGREGATION_TYPE_MAP}
            />
            <SearchSelectFormField
                name="unit"
                control={form.control}
                required={false}
                clearable
                label={formatMessage(unitsMessages.unit)}
                items={units?.results ?? []}
                placeholder={`${formatMessage(unitsMessages.unit)}...`}
                searchPlaceholder={formatMessage(unitsMessages.unit)}
                emptyTitle={formatMessage(unitsMessages.units)}
                getItemValue={(unit) => unit.id}
                getItemLabel={(unit) => `${unit.name} (${unit.abbreviation})`}
                getItemKeywords={(unit) => [unit.name, unit.code, unit.abbreviation]}
            />
            <FormDescription>{formatMessage(billableMetricsMessages.metricUnitHint)}</FormDescription>
            <TextareaFormField<BillableMetricFormSchemaValues>
                form={form}
                name="description"
                required={false}
                label={formatMessage(generalMessages.description)}
            />
            <SelectFormField<BillableMetricFormSchemaValues>
                form={form}
                name="is_active"
                isBoolean={true}
                label={formatMessage(voucherTypesMessages.isActive)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
        </Form>
    );
}

export default BillableMetricForm;
